import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Calendar, Users, Tag, User, Mail, Phone, CreditCard, Wallet } from "lucide-react";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BookingPage() {
  const { slug } = useParams();
  const loc = useLocation();
  const isBundle = loc.pathname.startsWith("/book/bundle/");
  const { user } = useAuth();
  const { currency, format } = useCurrency();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [pkg, setPkg] = useState(null);
  const [form, setForm] = useState({
    traveller_name: "", email: "", phone: "",
    travel_date: params.get("date") || "",
    passengers: 2, coupon: "", special_requests: ""
  });
  const [payMethod, setPayMethod] = useState("stripe");
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isBundle) {
      api.get(`/bundles/${slug}`).then((r) => {
        const b = r.data;
        setPkg({
          slug: b.slug, title: b.title, location: b.packages.map(p => p.location).join(" + "),
          duration: b.duration, image: b.image, price: b.bundle_price, isBundle: true,
        });
      });
    } else {
      api.get(`/packages/${slug}`).then((r) => setPkg(r.data.package));
    }
    api.get("/payments/razorpay/config").then((r) => setRazorpayReady(r.data.configured));
  }, [slug, isBundle]);
  useEffect(() => {
    if (user) setForm(f => ({ ...f, traveller_name: f.traveller_name || user.name, email: f.email || user.email, phone: f.phone || user.phone || "" }));
  }, [user]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = pkg ? pkg.price * form.passengers : 0;
  const discount = (form.coupon || "").toUpperCase() === "WELCOME10" ? Math.round(total * 0.1)
                  : (form.coupon || "").toUpperCase() === "PACKGO500" ? 500 * form.passengers : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const bookingPayload = isBundle
        ? { ...form, bundle_slug: slug, passengers: +form.passengers }
        : { ...form, package_slug: slug, passengers: +form.passengers };
      const { data: booking } = await api.post("/bookings", bookingPayload);

      if (payMethod === "razorpay") {
        if (!razorpayReady) {
          toast.error("Razorpay isn't set up yet. Please use Stripe or contact us on WhatsApp.");
          return;
        }
        const loaded = await loadRazorpayScript();
        if (!loaded) { toast.error("Failed to load Razorpay. Try again."); return; }
        const { data: order } = await api.post("/payments/razorpay/order", { booking_id: booking.id });
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "Your Trips",
          description: `${pkg.title}`,
          image: "/logo.png",
          prefill: {
            name: order.booking.traveller_name,
            email: order.booking.email,
            contact: order.booking.phone,
          },
          theme: { color: "#D4AF37" },
          handler: async (res) => {
            try {
              await api.post("/payments/razorpay/verify", {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
                booking_id: booking.id,
              });
              toast.success("Payment successful!");
              nav(`/payment/success?booking_id=${booking.id}`);
            } catch (err) {
              toast.error("Verification failed. Please contact support.");
            }
          },
          modal: { ondismiss: () => setSubmitting(false) },
        });
        rzp.open();
        return;
      }

      // Stripe default
      toast.success("Booking created! Redirecting to payment...");
      const { data: checkout } = await api.post("/payments/checkout", {
        package_slug: slug, booking_id: booking.id, origin_url: window.location.origin,
      });
      if (checkout.checkout_url) {
        window.location.href = checkout.checkout_url;
      } else {
        nav(`/payment/success?booking_id=${booking.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!pkg) return <div className="pt-32 min-h-screen text-center text-slate-500">Loading...</div>;

  return (
    <div className="pt-32 pb-24 bg-slate-50 dark:bg-slate-950 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} data-testid="booking-form" className="lg:col-span-2 rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Booking</div>
            <h1 className="text-3xl font-bold text-blue-950 dark:text-white mt-2">Complete Your Booking</h1>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field icon={User} label="Traveller name" value={form.traveller_name} onChange={change("traveller_name")} required data-testid="booking-name"/>
            <Field icon={Mail} label="Email" type="email" value={form.email} onChange={change("email")} required data-testid="booking-email"/>
            <Field icon={Phone} label="Phone" value={form.phone} onChange={change("phone")} required data-testid="booking-phone"/>
            <Field icon={Calendar} label="Travel date" type="date" value={form.travel_date} onChange={change("travel_date")} required data-testid="booking-date"/>
            <Field icon={Users} label="Passengers" type="number" min="1" max="20" value={form.passengers} onChange={change("passengers")} required data-testid="booking-passengers"/>
            <Field icon={Tag} label="Coupon (WELCOME10 / PACKGO500)" value={form.coupon} onChange={change("coupon")} data-testid="booking-coupon"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Special requests</label>
            <textarea data-testid="booking-notes" rows={3} value={form.special_requests} onChange={change("special_requests")}
              className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"/>
          </div>

          {/* Payment method */}
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Payment method</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <PayOption active={payMethod === "stripe"} onClick={()=>setPayMethod("stripe")} icon={CreditCard}
                title="Card (Stripe)" sub="Visa · Mastercard · Amex — worldwide"
                data-testid="pay-stripe"/>
              <PayOption active={payMethod === "razorpay"} onClick={()=>setPayMethod("razorpay")} icon={Wallet}
                title={`UPI / Net-banking (Razorpay)${razorpayReady ? "" : " · setup pending"}`}
                sub={razorpayReady ? "UPI · Cards · Wallets · Net-banking" : "Coming soon — use Stripe for now"}
                disabled={!razorpayReady} data-testid="pay-razorpay"/>
            </div>
          </div>

          <button data-testid="booking-submit" disabled={submitting} className="w-full py-4 rounded-full btn-gold text-sm disabled:opacity-60">
            {submitting ? "Processing..." : `Confirm & Pay ${format(total - discount)}`}
          </button>
        </form>

        <aside className="rounded-3xl p-8 bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 h-fit lg:sticky lg:top-28">
          <img src={pkg.image} alt="" className="rounded-2xl h-40 w-full object-cover"/>
          <h3 className="mt-4 text-xl font-bold text-blue-950 dark:text-white">{pkg.title}</h3>
          <div className="text-xs text-slate-500">{pkg.location} · {pkg.duration}</div>
          <div className="mt-4 space-y-1.5 text-sm">
            <Row l={`${format(pkg.price)} × ${form.passengers}`} r={format(total)}/>
            {discount > 0 && <Row l={`Coupon (${form.coupon.toUpperCase()})`} r={`- ${format(discount)}`} className="text-emerald-600"/>}
            <div className="h-px bg-slate-200 dark:bg-white/10 my-2"/>
            <Row l="Total" r={format(total - discount)} className="text-lg font-bold text-blue-950 dark:text-white"/>
          </div>
          {currency !== "INR" && (
            <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">Displayed in {currency} at live rates. Final charge is in INR ₹{(total - discount).toLocaleString('en-IN')}; your bank may apply a small FX fee.</div>
          )}
          <div className="mt-4 text-xs text-slate-500">✦ Confirmation email delivered instantly · Stripe test card 4242 4242 4242 4242</div>
        </aside>
      </div>
    </div>
  );
}

function PayOption({ active, onClick, icon: Icon, title, sub, disabled, ...rest }) {
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled}
      className={`text-left rounded-2xl p-4 border-2 flex gap-3 items-start ${
        active ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/40"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#D4AF37]/70"}`}
      {...rest}
    >
      <Icon className={`w-5 h-5 mt-0.5 ${active ? "text-[#D4AF37]" : "text-slate-400"}`}/>
      <div className="flex-1">
        <div className="text-sm font-semibold text-blue-950 dark:text-white">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
      </div>
      <span className={`w-4 h-4 rounded-full border-2 mt-1 ${active ? "border-[#D4AF37] bg-[#D4AF37]" : "border-slate-300 dark:border-white/20"}`}/>
    </button>
  );
}

function Field({ icon: Icon, label, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1 flex items-center gap-2 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4">
        <Icon className="w-4 h-4 text-slate-400"/>
        <input {...rest} className="w-full py-3 bg-transparent focus:outline-none text-sm"/>
      </div>
    </label>
  );
}
function Row({ l, r, className="" }) { return <div className={`flex justify-between ${className}`}><span>{l}</span><span>{r}</span></div>; }
